import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Loading,
  Typography
} from '@p81-common/ui';
import { WarningAttentionBox } from '../../../shared/ui';
import {
  ChildWorkspace,
  countCancelledSelected,
  cappedSelection,
  selectableCount,
  CANCELLED_WARNING_THRESHOLD,
  MAX_SELECTED_CHILDREN
} from './helpers';
import { createClasses } from './styles';

export interface SelectChildrenStepLabels {
  sectionTitle: string;
  infoBody: string;
  listTitle: string;
  selectAll: string;
  /** Shown instead of Select All when the list is longer than one batch. */
  selectFirst: string;
  unselectAll: string;
  selectedCount: string;
  limitReached: string;
  cancelledTag: string;
  cancelledWarningTitle: string;
  cancelledWarningBody: string;
  emptyState: string;
}

interface SelectChildrenStepProps {
  childWorkspaces: readonly ChildWorkspace[];
  isLoading?: boolean;
  selectedIds: readonly string[];
  onChange: (next: string[]) => void;
  labels: SelectChildrenStepLabels;
}

const SelectChildrenStep = ({
  childWorkspaces,
  isLoading,
  selectedIds,
  onChange,
  labels
}: SelectChildrenStepProps) => {
  const classes = createClasses();
  const selectedSet = new Set(selectedIds);
  const cancelledSelected = countCancelledSelected(
    childWorkspaces,
    selectedIds
  );
  const showCancelledWarning = cancelledSelected >= CANCELLED_WARNING_THRESHOLD;

  // Everything below counts against what one batch can carry, not against how
  // many children the parent has: those two numbers differ past 20.
  const selectable = selectableCount(childWorkspaces);
  const allSelected = selectable > 0 && selectedIds.length >= selectable;
  const atLimit = selectedIds.length >= MAX_SELECTED_CHILDREN;
  const exceedsOneBatch = childWorkspaces.length > MAX_SELECTED_CHILDREN;
  // Reaching the cap is only worth flagging when it actually cost the operator
  // something. On a parent with exactly 20 children, selecting all 20 hits the
  // limit with nothing left to block - warning there would tell someone who did
  // exactly the right thing to undo it, and point at choices that do not exist.
  const someRowsBlocked =
    atLimit && childWorkspaces.length > selectedIds.length;

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter(s => s !== id));
      return;
    }
    // Guard as well as disable: a click can still land on the label element
    // wrapping a disabled checkbox.
    if (atLimit) return;
    onChange([...selectedIds, id]);
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : cappedSelection(childWorkspaces));
  };

  const selectAllLabel = () => {
    if (allSelected) return labels.unselectAll;
    return exceedsOneBatch ? labels.selectFirst : labels.selectAll;
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box className={classes.loader}>
          <Loading />
        </Box>
      );
    }
    if (childWorkspaces.length === 0) {
      return (
        <Box className={classes.empty}>
          <Typography variant="body2">{labels.emptyState}</Typography>
        </Box>
      );
    }
    return (
      <Box className={classes.panel}>
        <Box className={classes.panelHeader}>
          <Typography variant="subtitle1" className={classes.panelTitle}>
            {labels.listTitle}{' '}
            <span className={classes.panelCount}>
              ({childWorkspaces.length})
            </span>
          </Typography>
          <Button
            clickArea={false}
            className={classes.selectAllBtn}
            onClick={toggleAll}
            size="small"
          >
            {selectAllLabel()}
          </Button>
        </Box>

        <Box
          className={classes.list}
          role="group"
          aria-label={labels.listTitle}
        >
          {childWorkspaces.map((child, index) => {
            const checked = selectedSet.has(child.id);
            const unavailable = atLimit && !checked;
            return (
              <React.Fragment key={child.id}>
                {index > 0 && <Divider className={classes.divider} />}
                <Box
                  component="label"
                  className={`${classes.row} ${
                    unavailable ? classes.rowUnavailable : ''
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={unavailable}
                    onChange={() => toggle(child.id)}
                  />
                  <Box className={classes.nameAndTag}>
                    <Typography variant="body2">{child.name}</Typography>
                    {child.cancelled && (
                      <Typography
                        component="span"
                        variant="caption"
                        className={classes.cancelledTag}
                      >
                        {labels.cancelledTag}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>

        {/* The count is the whole point of the cap, so it is always visible;
            the explanation appears once the ceiling has actually taken choices
            away - see someRowsBlocked.

            Both nodes render unconditionally and toggle their content instead:
            a live region has to be in the DOM before what it holds changes, so
            mounting the region and its text in one commit is announced
            unreliably. Reaching the cap also disables every unchecked row in
            the same commit, and this is the only thing that tells a screen
            reader the rest of the list just went dead. */}
        <Box
          className={`${classes.footer} ${
            someRowsBlocked ? classes.footerAtLimit : ''
          }`}
          aria-live="polite"
        >
          <Typography
            variant="caption"
            className={someRowsBlocked ? classes.countAtLimit : classes.count}
          >
            {labels.selectedCount}
          </Typography>
          <Typography variant="caption" className={classes.limitNote}>
            {someRowsBlocked ? labels.limitReached : ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="subtitle1" className={classes.title}>
          {labels.sectionTitle}
        </Typography>
        <Typography variant="body2" className={classes.infoBody}>
          {labels.infoBody}
        </Typography>
      </Box>

      {renderBody()}

      {!isLoading && showCancelledWarning && (
        <WarningAttentionBox
          title={labels.cancelledWarningTitle}
          subtitle={labels.cancelledWarningBody}
        />
      )}
    </Box>
  );
};

export default SelectChildrenStep;
